import type {
  Profile,
  GetBalanceResponse,
  GetKYCStatusResponse,
  GetVerifiedIdentityResponse,
  ListTransactionsResponse,
  ListWalletTransactionsResponse,
  ListUserWalletsResponse,
  NotificationPreferences,
  GetPortfolioResponse,
  ListNimMemoriesResponse,
  GetMemoryGraphResponse,
  GetEntityGraphResponse,
  GetFullGraphResponse,
  GetGraphStatsResponse,
  ExpandNodeResponse,
  ListLinkedAccountsResponse,
  ListRecipientsResponse,
  LinkedAccount,
  Recipient,
  LedgerAccount,
  ListLedgerAccountsResponse,
  GetUserDevicesResponse,
  ListInboxResponse,
  ListPaymentSchedulesResponse,
  ListPaymentScheduleJobsResponse,
  Transaction,
  AdminAnnouncement,
  AdminAnnouncementPush,
  AdminAudience,
  AdminResolveUsersResponse,
  AdminListAnnouncementsResponse,
  AdminGetAnnouncementDismissalsResponse,
  AdminPushAnnouncement,
  AdminListPushAnnouncementsResponse,
  AdminNimAnnouncement,
  AdminListNimAnnouncementsResponse,
  AdminSearchUsersResponse,
  AdminBatchResolveUsersByNameResponse,
  AdminBatchGetUsersResponse,
  ExportFilteredEmailsResponse,
  AdminListFFlagsResponse,
  AdminListFFlagOverridesResponse,
  AdminListConversationsResponse,
  AdminListNimMediaResponse,
  ListNimCallsResponse,
  PlaceNimCallRequest,
  PlaceNimCallResponse,
  AdminListFeedbackResponse,
  AdminGetConversationResponse,
  AdminListConversationFlagsResponse,
  ConversationFlag,
  Suspension,
  UserLifecycle,
  SendNudgePreviewRequest,
  SendNudgePreviewResponse,
  NudgeActivity,
  ReferralAttribution,
  RewardMerchant,
  RewardMerchantImage,
  RewardMerchantImageUpload,
  RewardBudget,
  ListRewardMerchantsResponse,
  ListRewardMerchantRevisionsResponse,
  ListRewardBudgetsResponse,
  ListRewardPaymentsResponse,
  QueryRewardProgrammeResponse,
  ResolveRewardMerchantLocationLinkResponse,
  EventGuest,
  EventGuestLookup,
  ListEventTicketIssuancesResponse,
} from '@/api/types';
import type {
  AdminSortDirection,
  AdminUserSortField,
  ConversationFlag_State,
  FeedbackKind,
  GraphNodeKind,
  NimCallInitiator,
} from '@/api/generated';
import { HttpError, NetworkError } from '@liminal/errors/http-errors';

const ADMIN_API_URL = import.meta.env.VITE_ADMIN_API_URL ?? '';
const V1 = '/admin/v1';

/**
 * The gateway's error envelope (LIM-2199), flattened from the gRPC status:
 * the taxonomy reason and category are what parseError branches on.
 */
interface ErrorEnvelope {
  readonly code?: number | string;
  readonly message?: string;
  readonly reason?: string;
  readonly category?: string;
  readonly details?: unknown[];
}

const parseEnvelope = (body: string): ErrorEnvelope | null => {
  try {
    const parsed: unknown = JSON.parse(body);
    return typeof parsed === 'object' && parsed !== null ? (parsed as ErrorEnvelope) : null;
  } catch {
    return null;
  }
};

class AdminClient {
  private baseUrl: string;
  private credential: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setCredential(credential: string) {
    this.credential = credential;
  }

  clearCredential() {
    this.credential = null;
  }

  /**
   * One request, throwing the same typed errors as the generated client
   * (common/js/api labs-client.ts executeApiCall): NetworkError when the
   * request never reached a server, HttpError carrying the gateway's envelope
   * otherwise, so parseError reads the taxonomy reason and category instead
   * of the body text that used to be stuffed into `new Error()`.
   */
  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.credential) {
      headers['Authorization'] = `Bearer ${this.credential}`;
    }

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        ...options,
        headers: { ...headers, ...options?.headers },
      });
    } catch (err) {
      // The browser's fetch rejects with a TypeError when no response came
      // back (offline, DNS, CORS, a reset); nothing else rejects here.
      if (err instanceof TypeError) throw new NetworkError(err.message);
      throw err;
    }

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      const envelope = parseEnvelope(body);
      const message = envelope?.message || body || response.statusText;
      throw new HttpError(message, response.status, undefined, {
        error: {
          code: envelope?.code === undefined ? undefined : String(envelope.code),
          reason: typeof envelope?.reason === 'string' ? envelope.reason : undefined,
          category: typeof envelope?.category === 'string' ? envelope.category : undefined,
          message,
          details: Array.isArray(envelope?.details) ? envelope.details : undefined,
        },
        status: response.status,
        statusText: response.statusText,
      });
    }

    return response.json();
  }

  // -- Auth --

  async getMe(): Promise<{ email: string }> {
    return this.request(`${V1}/me`);
  }

  async healthCheck(): Promise<{ status: string }> {
    return this.request(`${V1}/health`);
  }

  // -- Users --

  async searchUsers(params: {
    query?: string;
    limit?: number;
    offset?: number;
    kycStatusFilter?: string;
    platformFilter?: string;
    sortField?: AdminUserSortField;
    sortDirection?: AdminSortDirection;
  } = {}): Promise<AdminSearchUsersResponse> {
    const qs = new URLSearchParams();
    if (params.query) qs.set('query', params.query);
    qs.set('limit', String(params.limit ?? 25));
    qs.set('offset', String(params.offset ?? 0));
    if (params.kycStatusFilter) qs.set('kycStatusFilter', params.kycStatusFilter);
    if (params.platformFilter) qs.set('platformFilter', params.platformFilter);
    // Omitted rather than sent as the UNSPECIFIED member: the server reads an
    // absent sort as created-at descending, which is the same default.
    if (params.sortField) qs.set('sortField', params.sortField);
    if (params.sortDirection) qs.set('sortDirection', params.sortDirection);
    return this.request(`${V1}/users/search?${qs}`);
  }

  async exportFilteredEmails(params: {
    query?: string;
    kycStatusFilter?: string;
    platformFilter?: string;
  } = {}): Promise<ExportFilteredEmailsResponse> {
    const qs = new URLSearchParams();
    if (params.query) qs.set('query', params.query);
    if (params.kycStatusFilter) qs.set('kycStatusFilter', params.kycStatusFilter);
    if (params.platformFilter) qs.set('platformFilter', params.platformFilter);
    return this.request(`${V1}/users/emails?${qs}`);
  }

  async getUser(userId: string): Promise<Profile> {
    return this.request(`${V1}/users/${userId}`);
  }

  // Resolve a pasted list of mixed user ids and emails to profiles, reporting
  // identifiers that matched nothing. Used by the announcement audience selector.
  async resolveUsers(identifiers: string[]): Promise<AdminResolveUsersResponse> {
    return this.request(`${V1}/users/resolve`, {
      method: 'POST',
      body: JSON.stringify({ identifiers }),
    });
  }

  // Resolve many lowercased "first last" names to profiles in one call
  // (exact full-name match; unmatched names omitted). LIM-1832.
  async adminBatchResolveUsersByName(names: string[]): Promise<Record<string, Profile>> {
    const resp = await this.request<AdminBatchResolveUsersByNameResponse>(`${V1}/users/batch-resolve`, {
      method: 'POST',
      body: JSON.stringify({ names }),
    });
    return resp.profiles ?? {};
  }

  // Fetch many profiles by user_id in one call (keyed by user_id; unknown ids
  // omitted). LIM-1832.
  async adminBatchGetUsers(userIds: string[]): Promise<Record<string, Profile>> {
    const resp = await this.request<AdminBatchGetUsersResponse>(`${V1}/users/batch-get`, {
      method: 'POST',
      body: JSON.stringify({ userIds }),
    });
    return resp.profiles ?? {};
  }

  async getUserBalances(userId: string): Promise<GetBalanceResponse> {
    return this.request(`${V1}/users/${userId}/balances`);
  }

  async getUserWallets(userId: string, chainId?: number): Promise<ListUserWalletsResponse> {
    const params = new URLSearchParams();
    if (chainId) params.set('chainId', String(chainId));
    const qs = params.toString();
    return this.request(`${V1}/users/${userId}/wallets${qs ? `?${qs}` : ''}`);
  }

  async getUserSavings(userId: string): Promise<GetBalanceResponse> {
    return this.request(`${V1}/users/${userId}/savings`);
  }

  async getUserKycStatus(userId: string): Promise<GetKYCStatusResponse> {
    return this.request(`${V1}/users/${userId}/kyc/status`);
  }

  async getUserKycData(userId: string): Promise<GetVerifiedIdentityResponse> {
    return this.request(`${V1}/users/${userId}/kyc/data`);
  }

  // context/status take the ledger's TRANSACTION_CONTEXT_* / TRANSACTION_STATUS_*
  // enum names; assetSymbol is matched exactly (uppercase). pageSize is capped
  // at 100 server-side, and pageToken is the cursor from the previous response.
  async getUserLedgerTransactions(userId: string, params: {
    context?: string;
    status?: string;
    assetSymbol?: string;
    pageSize?: number;
    pageToken?: string;
  } = {}): Promise<ListTransactionsResponse> {
    const qs = new URLSearchParams();
    if (params.context) qs.set('context', params.context);
    if (params.status) qs.set('status', params.status);
    if (params.assetSymbol) qs.set('assetSymbol', params.assetSymbol);
    qs.set('pageSize', String(params.pageSize ?? 25));
    if (params.pageToken) qs.set('pageToken', params.pageToken);
    return this.request(`${V1}/users/${userId}/transactions/ledger?${qs}`);
  }

  // chainId is REQUIRED, not optional: wallets.ListTransactionsRequest declares
  // `chain_id` as `gt: 0`, so the validating interceptor rejects a request
  // without one before it reaches the handler. Omitting it used to make this
  // call fail on every user.
  // status is the wallets service's own lowercase vocabulary
  // ("pending" | "success" | "failed"), not a ledger enum.
  async getUserWalletTransactions(userId: string, params: {
    chainId: number;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListWalletTransactionsResponse> {
    const qs = new URLSearchParams();
    qs.set('chainId', String(params.chainId));
    if (params.status) qs.set('status', params.status);
    qs.set('limit', String(params.limit ?? 25));
    qs.set('offset', String(params.offset ?? 0));
    return this.request(`${V1}/users/${userId}/transactions/wallet?${qs}`);
  }

  async getUserPreferences(userId: string): Promise<NotificationPreferences> {
    return this.request(`${V1}/users/${userId}/preferences`);
  }

  async getUserPortfolio(userId: string): Promise<GetPortfolioResponse> {
    return this.request(`${V1}/users/${userId}/portfolio`);
  }

  // -- Nim --

  async listNimMemories(params: {
    pageSize?: number;
    pageToken?: string;
    ownerIdFilter?: string;
  } = {}): Promise<ListNimMemoriesResponse> {
    const qs = new URLSearchParams();
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params.pageToken) qs.set('pageToken', params.pageToken);
    if (params.ownerIdFilter) qs.set('ownerIdFilter', params.ownerIdFilter);
    return this.request(`${V1}/nim/memories?${qs}`);
  }

  async getNimMemoryGraph(params: {
    ownerId: string;
    seedMemoryId?: string;
    depth?: number;
    pathLimit?: number;
  }): Promise<GetMemoryGraphResponse> {
    const qs = new URLSearchParams();
    qs.set('ownerId', params.ownerId);
    if (params.seedMemoryId) qs.set('seedMemoryId', params.seedMemoryId);
    if (params.depth) qs.set('depth', String(params.depth));
    if (params.pathLimit) qs.set('pathLimit', String(params.pathLimit));
    return this.request(`${V1}/nim/graph/memory?${qs}`);
  }

  async getNimEntityGraph(params: {
    ownerIdFilter?: string;
    pathLimit?: number;
    minConfidence?: number;
  } = {}): Promise<GetEntityGraphResponse> {
    const qs = new URLSearchParams();
    if (params.ownerIdFilter) qs.set('ownerIdFilter', params.ownerIdFilter);
    if (params.pathLimit) qs.set('pathLimit', String(params.pathLimit));
    if (params.minConfidence !== undefined) qs.set('minConfidence', String(params.minConfidence));
    return this.request(`${V1}/nim/graph/entity?${qs}`);
  }

  async getNimGraphStats(params: {
    ownerIdFilter?: string;
  } = {}): Promise<GetGraphStatsResponse> {
    const qs = new URLSearchParams();
    if (params.ownerIdFilter) qs.set('ownerIdFilter', params.ownerIdFilter);
    return this.request(`${V1}/nim/graph/stats?${qs}`);
  }

  async getNimFullGraph(params: {
    ownerId: string;
  }): Promise<GetFullGraphResponse> {
    const qs = new URLSearchParams();
    qs.set('ownerId', params.ownerId);
    return this.request(`${V1}/nim/graph/full?${qs}`);
  }

  async expandNimNode(params: {
    ownerId?: string;
    kind: GraphNodeKind;
    nodeId: string;
    depth?: number;
    pathLimit?: number;
  }): Promise<ExpandNodeResponse> {
    const qs = new URLSearchParams();
    if (params.ownerId) qs.set('ownerId', params.ownerId);
    qs.set('kind', params.kind);
    qs.set('nodeId', params.nodeId);
    if (params.depth) qs.set('depth', String(params.depth));
    if (params.pathLimit) qs.set('pathLimit', String(params.pathLimit));
    return this.request(`${V1}/nim/graph/expand?${qs}`);
  }

  async getUserLinkedAccounts(userId: string): Promise<ListLinkedAccountsResponse> {
    return this.request(`${V1}/users/${userId}/linked-accounts`);
  }

  async getUserRecipients(userId: string): Promise<ListRecipientsResponse> {
    return this.request(`${V1}/users/${userId}/recipients`);
  }

  // -- Notifications --

  async getUserDevices(userId: string): Promise<GetUserDevicesResponse> {
    return this.request(`${V1}/users/${userId}/devices`);
  }

  async getUserInbox(userId: string, limit = 50): Promise<ListInboxResponse> {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    return this.request(`${V1}/users/${userId}/inbox?${params}`);
  }

  // -- Onboarding nudges (LIM-3551) --

  async getUserLifecycle(userId: string): Promise<UserLifecycle> {
    return this.request(`${V1}/users/${userId}/lifecycle`);
  }

  async restartUserLifecycle(userId: string): Promise<UserLifecycle> {
    return this.request(`${V1}/users/${userId}/lifecycle/restart`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async sendNudgePreview(request: SendNudgePreviewRequest): Promise<SendNudgePreviewResponse> {
    return this.request(`${V1}/lifecycle/preview`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async summarizeNudgeActivity(): Promise<NudgeActivity> {
    return this.request(`${V1}/lifecycle/activity`);
  }

  // -- Referrals (LIM-4840) --

  async summarizeReferralAttribution(): Promise<ReferralAttribution> {
    return this.request(`${V1}/referrals:summarizeAttribution`);
  }

  // -- Scheduled Payments --

  async getUserSchedules(userId: string): Promise<ListPaymentSchedulesResponse> {
    return this.request(`${V1}/users/${userId}/schedules`);
  }

  async getScheduleJobs(userId: string, scheduleId: string): Promise<ListPaymentScheduleJobsResponse> {
    return this.request(`${V1}/users/${userId}/schedules/${scheduleId}/jobs`);
  }

  // -- Global --

  async listTransactions(filters?: {
    accountId?: string;
    context?: string;
    status?: string;
    assetSymbol?: string;
    pageSize?: number;
    pageToken?: string;
  }): Promise<ListTransactionsResponse> {
    const params = new URLSearchParams();
    if (filters?.accountId) params.set('accountId', filters.accountId);
    if (filters?.context) params.set('context', filters.context);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.assetSymbol) params.set('assetSymbol', filters.assetSymbol);
    params.set('pageSize', String(filters?.pageSize ?? 50));
    if (filters?.pageToken) params.set('pageToken', filters.pageToken);
    return this.request(`${V1}/transactions?${params}`);
  }

  async getTransaction(id: string): Promise<Transaction> {
    return this.request(`${V1}/transactions/${id}`);
  }

  // -- Ledger accounts --

  async listLedgerAccounts(filters?: {
    pageSize?: number;
    pageToken?: string;
  }): Promise<ListLedgerAccountsResponse> {
    const params = new URLSearchParams();
    params.set('pageSize', String(filters?.pageSize ?? 50));
    if (filters?.pageToken) params.set('pageToken', filters.pageToken);
    return this.request(`${V1}/accounts?${params}`);
  }

  // System account names can carry reserved characters ("investing:brokerage");
  // encoding keeps the gateway from reading the colon as a custom-verb separator.
  async getLedgerAccount(accountId: string): Promise<LedgerAccount> {
    return this.request(`${V1}/accounts/${encodeURIComponent(accountId)}`);
  }

  async getRampRecipient(recipientId: string): Promise<Recipient> {
    return this.request(`${V1}/ramps/recipients/${encodeURIComponent(recipientId)}`);
  }

  async getRampLinkedAccount(accountId: string): Promise<LinkedAccount> {
    return this.request(`${V1}/ramps/linked-accounts/${encodeURIComponent(accountId)}`);
  }

  // -- Conversations (Nim, cross-user admin view) --

  async listConversations(params: {
    query?: string;
    pageSize?: number;
    pageToken?: string;
  } = {}): Promise<AdminListConversationsResponse> {
    const qs = new URLSearchParams();
    if (params.query) qs.set('query', params.query);
    qs.set('pageSize', String(params.pageSize ?? 25));
    if (params.pageToken) qs.set('pageToken', params.pageToken);
    return this.request(`${V1}/conversations?${qs}`);
  }

  async getConversation(conversationId: string): Promise<AdminGetConversationResponse> {
    return this.request(`${V1}/conversations/${conversationId}`);
  }

  // -- Nim conversation flags (the team's feedback on Nim's replies) --

  // Newest first. Omitting the state lists every state, and omitting the
  // conversation lists every conversation's flags; the page token is refused
  // under other filters than its own.
  async listConversationFlags(params: {
    state?: ConversationFlag_State;
    conversationId?: string;
    pageSize?: number;
    pageToken?: string;
  } = {}): Promise<AdminListConversationFlagsResponse> {
    const qs = new URLSearchParams();
    if (params.state) qs.set('state', params.state);
    if (params.conversationId) qs.set('conversationId', params.conversationId);
    qs.set('pageSize', String(params.pageSize ?? 50));
    if (params.pageToken) qs.set('pageToken', params.pageToken);
    return this.request(`${V1}/conversation-flags?${qs}`);
  }

  // Raised as the signed-in admin: the gateway stamps the reporter and the
  // source from the session. Omit messageId to flag the whole conversation.
  // The same idempotencyKey again returns the flag it raised rather than
  // raising another.
  async createConversationFlag(
    conversationId: string,
    data: {
      messageId?: string;
      feedback: string;
      idempotencyKey: string; // minted per composer submission; makes retries safe
    },
  ): Promise<ConversationFlag> {
    return this.request(`${V1}/conversations/${encodeURIComponent(conversationId)}/flags`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Moves a flag through review. Omitting reviewNotes leaves the current
  // notes; an empty string clears them.
  async updateConversationFlag(
    flagId: string,
    data: {
      state: ConversationFlag_State;
      reviewNotes?: string;
    },
  ): Promise<ConversationFlag> {
    return this.request(`${V1}/conversation-flags/${encodeURIComponent(flagId)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // -- Nim media (images uploaded into conversations) --

  async listNimMedia(params: {
    query?: string;
    pageSize?: number;
    pageToken?: string;
    oldestFirst?: boolean;
  } = {}): Promise<AdminListNimMediaResponse> {
    const qs = new URLSearchParams();
    if (params.query) qs.set('query', params.query);
    qs.set('pageSize', String(params.pageSize ?? 24));
    if (params.pageToken) qs.set('pageToken', params.pageToken);
    if (params.oldestFirst) qs.set('oldestFirst', 'true');
    return this.request(`${V1}/nim/media?${qs}`);
  }

  // -- Nim calls (LIM-4588) --

  // Rings the user's phones as the signed-in operator (the gateway reads who
  // from the session). A refusal comes back as an HttpError carrying the
  // voice service's reason. The same requestId again returns the call it
  // placed rather than placing another.
  async placeNimCall(request: PlaceNimCallRequest): Promise<PlaceNimCallResponse> {
    return this.request(`${V1}/nim/calls:place`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Newest first. Omitting the initiator lists calls whoever started or
  // placed them; the page token is refused under other filters than its own.
  async listNimCalls(params: {
    userId?: string;
    initiator?: NimCallInitiator;
    pageSize?: number;
    pageToken?: string;
  } = {}): Promise<ListNimCallsResponse> {
    const qs = new URLSearchParams();
    if (params.userId) qs.set('userId', params.userId);
    if (params.initiator) qs.set('initiator', params.initiator);
    qs.set('pageSize', String(params.pageSize ?? 50));
    if (params.pageToken) qs.set('pageToken', params.pageToken);
    return this.request(`${V1}/nim/calls?${qs}`);
  }

  // -- Nim feedback (bug reports / feature requests raised through Nim) --

  async listFeedback(params: {
    query?: string;
    kinds?: FeedbackKind[];
    pageSize?: number;
    pageToken?: string;
  } = {}): Promise<AdminListFeedbackResponse> {
    const qs = new URLSearchParams();
    if (params.query) qs.set('query', params.query);
    // Repeated proto fields arrive as repeated query params, not a CSV.
    for (const kind of params.kinds ?? []) qs.append('kinds', kind);
    qs.set('pageSize', String(params.pageSize ?? 25));
    if (params.pageToken) qs.set('pageToken', params.pageToken);
    return this.request(`${V1}/feedback?${qs}`);
  }

  // -- Announcements --

  async listAnnouncements(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<AdminListAnnouncementsResponse> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    qs.set('limit', String(params?.limit ?? 50));
    qs.set('offset', String(params?.offset ?? 0));
    return this.request(`${V1}/announcements?${qs}`);
  }

  async createAnnouncement(
    data: Omit<AdminAnnouncement, 'id' | 'createdAt' | 'updatedAt'> & {
      audience?: AdminAudience;
      push?: AdminAnnouncementPush;
    },
  ): Promise<AdminAnnouncement> {
    return this.request(`${V1}/announcements`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAnnouncement(
    id: string,
    data: Partial<Omit<AdminAnnouncement, 'id' | 'createdAt'>> & { audience?: AdminAudience },
  ): Promise<AdminAnnouncement> {
    return this.request(`${V1}/announcements/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...data, id }),
    });
  }

  async deleteAnnouncement(id: string): Promise<void> {
    return this.request(`${V1}/announcements/${id}`, { method: 'DELETE' });
  }

  async getAnnouncementDismissals(announcementId: string): Promise<AdminGetAnnouncementDismissalsResponse> {
    return this.request(`${V1}/announcements/${announcementId}/dismissals`);
  }

  // -- Push announcements --

  async listPushAnnouncements(params?: {
    limit?: number;
    offset?: number;
  }): Promise<AdminListPushAnnouncementsResponse> {
    const qs = new URLSearchParams();
    qs.set('limit', String(params?.limit ?? 50));
    qs.set('offset', String(params?.offset ?? 0));
    return this.request(`${V1}/push-announcements?${qs}`);
  }

  async createPushAnnouncement(data: {
    title: string;
    message: string;
    url?: string; // internal app path opened on tap (e.g. /savings)
    sendAt?: string; // RFC3339; omit to send immediately
    audience?: AdminAudience; // omit to send to all users
  }): Promise<AdminPushAnnouncement> {
    return this.request(`${V1}/push-announcements`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async cancelPushAnnouncement(id: string): Promise<AdminPushAnnouncement> {
    return this.request(`${V1}/push-announcements/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // -- Nim announcements --

  async listNimAnnouncements(params?: {
    limit?: number;
    offset?: number;
  }): Promise<AdminListNimAnnouncementsResponse> {
    const qs = new URLSearchParams();
    qs.set('limit', String(params?.limit ?? 50));
    qs.set('offset', String(params?.offset ?? 0));
    return this.request(`${V1}/nim-announcements?${qs}`);
  }

  async createNimAnnouncement(data: {
    content: string;
    audience?: AdminAudience; // omit to send to all users
    idempotencyKey: string; // minted per form submission; makes retries safe
    buttonLabel?: string; // set with buttonUri, or neither
    buttonUri?: string; // https only
  }): Promise<AdminNimAnnouncement> {
    return this.request(`${V1}/nim-announcements`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async cancelNimAnnouncement(id: string): Promise<AdminNimAnnouncement> {
    return this.request(`${V1}/nim-announcements/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // -- Feature flags --

  async listFFlags(): Promise<AdminListFFlagsResponse> {
    return this.request(`${V1}/fflags/list`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async enableFFlag(name: string): Promise<void> {
    return this.request(`${V1}/fflags/enable`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async disableFFlag(name: string): Promise<void> {
    return this.request(`${V1}/fflags/disable`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async listFFlagOverrides(params: { userId?: string; fflagName?: string } = {}): Promise<AdminListFFlagOverridesResponse> {
    return this.request(`${V1}/fflags/overrides/list`, {
      method: 'POST',
      body: JSON.stringify({
        userId: params.userId ?? '',
        fflagName: params.fflagName ?? '',
      }),
    });
  }

  async setFFlagOverride(params: { name: string; userId: string; enabled: boolean }): Promise<void> {
    return this.request(`${V1}/fflags/overrides/set`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async deleteFFlagOverride(params: { name: string; userId: string }): Promise<void> {
    return this.request(`${V1}/fflags/overrides/delete`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // -- User suspensions (LIM-2453) --

  async getSuspension(userId: string): Promise<Suspension> {
    return this.request(`${V1}/users/${userId}/suspension`);
  }

  // Exactly one of suspendedUntil (RFC 3339, must be in the future) or
  // permanent. actorEmail is stamped server-side from the admin identity.
  async updateSuspension(params: {
    userId: string;
    suspendedUntil?: string;
    permanent?: boolean;
    reason: string;
  }): Promise<Suspension> {
    const { userId, ...body } = params;
    return this.request(`${V1}/users/${userId}/suspension`, {
      method: 'PATCH',
      body: JSON.stringify({ userId, ...body }),
    });
  }

  async deleteSuspension(params: { userId: string; reason: string }): Promise<void> {
    const query = new URLSearchParams({ reason: params.reason });
    return this.request(`${V1}/users/${params.userId}/suspension?${query}`, {
      method: 'DELETE',
    });
  }

  // -- Partner Rewards (LIM-4264) --
  //
  // Paths take the resource id, the last segment of the resource name. Every
  // change carries the etag the operator read; the gateway stamps the
  // operator as the revision's actor and mints the idempotency key.

  async listRewardMerchants(params: { pageSize?: number; pageToken?: string; filter?: string } = {}): Promise<ListRewardMerchantsResponse> {
    const qs = new URLSearchParams();
    qs.set('pageSize', String(params.pageSize ?? 50));
    if (params.pageToken) qs.set('pageToken', params.pageToken);
    if (params.filter) qs.set('filter', params.filter);
    return this.request(`${V1}/rewardMerchants?${qs}`);
  }

  async getRewardMerchant(id: string): Promise<RewardMerchant> {
    return this.request(`${V1}/rewardMerchants/${id}`);
  }

  // Every rewards write carries the caller's idempotency key: in the query
  // string where the body is the resource (create, update), in the body
  // otherwise. A retry with the same key replays the first result.
  async createRewardMerchant(merchant: Omit<RewardMerchant, 'name'>, requestId: string): Promise<RewardMerchant> {
    const qs = new URLSearchParams({ requestId });
    return this.request(`${V1}/rewardMerchants?${qs}`, {
      method: 'POST',
      body: JSON.stringify(merchant),
    });
  }

  // Sends the whole merchant with the fields to change named in the mask.
  async updateRewardMerchant(id: string, merchant: RewardMerchant, updateMask: readonly string[], requestId: string): Promise<RewardMerchant> {
    const qs = new URLSearchParams({ updateMask: updateMask.join(','), requestId });
    return this.request(`${V1}/rewardMerchants/${id}?${qs}`, {
      method: 'PATCH',
      body: JSON.stringify(merchant),
    });
  }

  async listRewardMerchantRevisions(id: string, params: { pageSize?: number; pageToken?: string } = {}): Promise<ListRewardMerchantRevisionsResponse> {
    const qs = new URLSearchParams();
    qs.set('pageSize', String(params.pageSize ?? 50));
    if (params.pageToken) qs.set('pageToken', params.pageToken);
    return this.request(`${V1}/rewardMerchants/${id}:listRevisions?${qs}`);
  }

  async changeRewardMerchantState(
    id: string,
    change: 'pause' | 'resume' | 'retire',
    params: { etag: string; reason: string; requestId: string },
  ): Promise<RewardMerchant> {
    return this.request(`${V1}/rewardMerchants/${id}:${change}`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // Reads the coordinates and place name out of a Google Maps link. The
  // server follows a short link (maps.app.goo.gl), which the browser cannot
  // for CORS. It reads only, so it takes no idempotency key.
  async resolveRewardMerchantLocationLink(uri: string): Promise<ResolveRewardMerchantLocationLinkResponse> {
    return this.request(`${V1}/rewardMerchants:resolveLocationLink`, {
      method: 'POST',
      body: JSON.stringify({ uri }),
    });
  }

  // -- Partner images (LIM-4606) --
  //
  // The bytes never pass through the gateway: the browser PUTs them to the
  // URL createRewardMerchantImageUpload signs (lib/signedUpload.ts), then
  // registers them with createRewardMerchantImage. A registered image shows
  // once an update places it in the merchant's `images` or names it `logo`.

  async createRewardMerchantImageUpload(
    merchantId: string,
    upload: { mimeType: string; sizeBytes: number },
  ): Promise<RewardMerchantImageUpload> {
    return this.request(`${V1}/rewardMerchants/${merchantId}/images:upload`, {
      method: 'POST',
      body: JSON.stringify(upload),
    });
  }

  // The request id is minted once per upload and sent again on a retry, so a
  // retried registration makes one image.
  async createRewardMerchantImage(merchantId: string, uploadId: string, requestId: string): Promise<RewardMerchantImage> {
    const qs = new URLSearchParams({ requestId });
    return this.request(`${V1}/rewardMerchants/${merchantId}/images?${qs}`, {
      method: 'POST',
      body: JSON.stringify({ uploadId }),
    });
  }

  // A retry with the same request id succeeds once the image is gone rather
  // than failing as not found. The merchant's current logo is refused.
  async deleteRewardMerchantImage(merchantId: string, imageId: string, requestId: string): Promise<void> {
    const qs = new URLSearchParams({ requestId });
    return this.request(`${V1}/rewardMerchants/${merchantId}/images/${imageId}?${qs}`, {
      method: 'DELETE',
    });
  }

  async listRewardBudgets(params: { pageSize?: number; pageToken?: string; filter?: string } = {}): Promise<ListRewardBudgetsResponse> {
    const qs = new URLSearchParams();
    qs.set('pageSize', String(params.pageSize ?? 50));
    if (params.pageToken) qs.set('pageToken', params.pageToken);
    if (params.filter) qs.set('filter', params.filter);
    return this.request(`${V1}/rewardBudgets?${qs}`);
  }

  async getRewardBudget(id: string): Promise<RewardBudget> {
    return this.request(`${V1}/rewardBudgets/${id}`);
  }

  async createRewardBudget(budget: Omit<RewardBudget, 'name'>, requestId: string): Promise<RewardBudget> {
    const qs = new URLSearchParams({ requestId });
    return this.request(`${V1}/rewardBudgets?${qs}`, {
      method: 'POST',
      body: JSON.stringify(budget),
    });
  }

  async updateRewardBudget(id: string, budget: RewardBudget, updateMask: readonly string[], requestId: string): Promise<RewardBudget> {
    const qs = new URLSearchParams({ updateMask: updateMask.join(','), requestId });
    return this.request(`${V1}/rewardBudgets/${id}?${qs}`, {
      method: 'PATCH',
      body: JSON.stringify(budget),
    });
  }

  async changeRewardBudgetState(
    id: string,
    change: 'pause' | 'resume' | 'close',
    params: { etag: string; requestId: string },
  ): Promise<RewardBudget> {
    return this.request(`${V1}/rewardBudgets/${id}:${change}`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // -- Partner Rewards payments and the programme (LIM-4279) --

  async listRewardPayments(params: { pageSize?: number; pageToken?: string; filter?: string } = {}): Promise<ListRewardPaymentsResponse> {
    const qs = new URLSearchParams();
    qs.set('pageSize', String(params.pageSize ?? 50));
    if (params.pageToken) qs.set('pageToken', params.pageToken);
    if (params.filter) qs.set('filter', params.filter);
    return this.request(`${V1}/rewardPayments?${qs}`);
  }

  async queryRewardProgramme(): Promise<QueryRewardProgrammeResponse> {
    return this.request(`${V1}/rewardProgramme:query`);
  }

  // -- Event check-in (LIM-4707) --
  //
  // An event is config in partner_service, addressed by its code. The gateway
  // records the signed-in operator as who handed the tickets over; the client
  // never sends it.

  // Finds a guest of the event by exactly one of their tag (current or
  // previous, any case), email address or user id.
  async resolveEventGuest(eventCode: string, lookup: EventGuestLookup): Promise<EventGuest> {
    const qs = new URLSearchParams({ [lookup.by]: lookup.value });
    return this.request(`${V1}/events/${encodeURIComponent(eventCode)}/guests:resolve?${qs}`);
  }

  // Hands the guest all of the event's tickets. Keyed on the event and the
  // guest, so it takes no request id: a repeat records nothing and is refused
  // with EVENT_TICKETS_ALREADY_ISSUED.
  async issueEventTickets(eventCode: string, userId: string): Promise<EventGuest> {
    return this.request(
      `${V1}/events/${encodeURIComponent(eventCode)}/guests/${encodeURIComponent(userId)}:issueTickets`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  // Most recent first, with the event's totals over every page.
  async listEventTicketIssuances(
    eventCode: string,
    params: { pageSize?: number; pageToken?: string } = {},
  ): Promise<ListEventTicketIssuancesResponse> {
    const qs = new URLSearchParams();
    if (params.pageSize !== undefined) qs.set('pageSize', String(params.pageSize));
    if (params.pageToken) qs.set('pageToken', params.pageToken);
    return this.request(`${V1}/events/${encodeURIComponent(eventCode)}/ticketIssuances?${qs}`);
  }

  // -- Profile --

  // Writes the given profile fields; returns the updated profile. Send only
  // the fields to change, keyed by their JSON names: the gateway derives the
  // update mask from the body's keys, so a field sent as false or empty is
  // written too.
  async updateProfile(userId: string, fields: Partial<Profile>): Promise<Profile> {
    return this.request(`${V1}/users/${userId}/profile`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    });
  }

}

export const adminClient = new AdminClient(ADMIN_API_URL);
